import { Dialog, DialogActions, DialogContent, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ContainedButton } from "components/ContainedButton";
import SelectField from "components/select-field";
import TextField from "components/text-field";
import { FC, useRef } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { OrderProperty } from "types/order";
import { useCreateProrationModal } from "./hooks";
import { useStyles } from "./styles";

type Props = {
  selectedProperty: OrderProperty,
  onClose: () => void,
  onCreated?: (proratedId: string) => void
}

export const CreateProrationModal: FC<Props> = ({
  selectedProperty,
  onClose,
  onCreated
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const descriptionInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const {
    isLoading,
    prorationForm,
    onConfirm
  } = useCreateProrationModal({
    selectedProperty,
    onClose,
    onCreated
  });
  const { control } = prorationForm;

  return (
    <Dialog
      open
      fullWidth
      fullScreen={isMobile}
      maxWidth="sm"
      disableAutoFocus
      TransitionProps={{ onEntered: () => descriptionInputRef.current?.focus() }}
    >
      <DialogContent style={{ padding: 0 }}>
        <Grid container direction="column">
          <Grid item>
            <Typography variant="h5" className={classes.title}>
              {t('orders:createProrationTitle')}
            </Typography>
          </Grid>
          <Grid item container className={classes.container}>
            <Grid item xs={12}>
              <Typography variant="body2">
                {t('orders:propertyLabel')}
              </Typography>
              <Typography>{selectedProperty.address.address}</Typography>
            </Grid>

            <Controller
              control={control}
              name="description"
              rules={{
                validate: (value) => value.trim().length > 0 || t('orders:requiredField'),
              }}
              render={({ field, fieldState }) => (
                <TextField
                  color="secondary"
                  label={<Typography variant="body2">{t('common:description')}</Typography>}
                  name="description"
                  handleBlur={field.onBlur}
                  value={field.value}
                  onChange={field.onChange}
                  inputRef={descriptionInputRef}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="kind"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <SelectField
                      label={t('orders:kindLabel')}
                      value={field.value}
                      handleBlur={field.onBlur}
                      handleChange={field.onChange}
                      data={[
                        {
                          _id: 'installment',
                          value: t('orders:installmentOption')
                        },
                        {
                          _id: 'per_diem',
                          value: t('orders:perDiemOption')
                        }
                      ]}
                      dataKey="_id"
                      dataValue="_id"
                      dataText="value"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="type"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <SelectField
                      label={t('orders:typeLabel')}
                      value={field.value}
                      handleBlur={field.onBlur}
                      handleChange={field.onChange}
                      data={[
                        {
                          _id: 'basic',
                          value: t('orders:basicOption')
                        },
                        {
                          _id: 'tax',
                          value: t('orders:taxOption')
                        }
                      ]}
                      dataKey="_id"
                      dataValue="_id"
                      dataText="value"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actionButtons}>
        <ContainedButton
          text={t('orders:closeButton')}
          onClick={onClose}
          disabled={isLoading}
        />
        <ContainedButton
          text={t('orders:createButton')}
          isLoading={isLoading}
          onClick={onConfirm}
        />
      </DialogActions>
    </Dialog>
  );
};
